/**
 * Client telemetry beacon — batches lightweight browser metrics and POSTs them
 * to /api/telemetry/beacon once per minute (and on tab hide via sendBeacon).
 *
 * Collected:
 *   - page-load timing (Navigation Timing API, reported once per page load)
 *   - /api request latency as seen from the browser (fetch wrapper)
 *   - uncaught JS error / unhandled rejection counts
 *
 * The server aggregates batches into the telemetry_samples 'client' family.
 * Silent by design: failures are swallowed, nothing here may break the app.
 */

const FLUSH_INTERVAL_MS = 60_000;

interface Window_ {
  pageLoads: { count: number; totalMs: number };
  api: { count: number; totalMs: number; maxMs: number };
  jsErrors: number;
}

let win: Window_ = {
  pageLoads: { count: 0, totalMs: 0 },
  api: { count: 0, totalMs: 0, maxMs: 0 },
  jsErrors: 0,
};

let started = false;

function hasData(): boolean {
  return win.pageLoads.count > 0 || win.api.count > 0 || win.jsErrors > 0;
}

function buildPayload(): Record<string, unknown> {
  const payload: Record<string, unknown> = {};
  if (win.pageLoads.count > 0) payload.pageLoads = { count: win.pageLoads.count, totalMs: Math.round(win.pageLoads.totalMs) };
  if (win.api.count > 0) payload.api = { count: win.api.count, totalMs: Math.round(win.api.totalMs), maxMs: Math.round(win.api.maxMs) };
  if (win.jsErrors > 0) payload.jsErrors = win.jsErrors;
  return payload;
}

function reset(): void {
  win = { pageLoads: { count: 0, totalMs: 0 }, api: { count: 0, totalMs: 0, maxMs: 0 }, jsErrors: 0 };
}

function flush(useBeacon = false): void {
  if (!hasData()) return;
  const body = JSON.stringify(buildPayload());
  reset();
  try {
    if (useBeacon && navigator.sendBeacon) {
      navigator.sendBeacon("/api/telemetry/beacon", new Blob([body], { type: "application/json" }));
      return;
    }
    void fetch("/api/telemetry/beacon", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      credentials: "include",
      body,
      keepalive: true,
    }).catch(() => {});
  } catch {
    // never let telemetry break the app
  }
}

function recordPageLoad(): void {
  try {
    const [nav] = performance.getEntriesByType("navigation") as PerformanceNavigationTiming[];
    if (nav && nav.loadEventEnd > 0) {
      win.pageLoads.count++;
      win.pageLoads.totalMs += nav.loadEventEnd - nav.startTime;
    }
  } catch {}
}

/** Start the beacon. Safe to call multiple times — only the first call wires up. */
export function startTelemetryBeacon(): void {
  if (started || typeof window === "undefined") return;
  started = true;

  // Page load timing (may not be complete yet on early boot)
  if (document.readyState === "complete") {
    recordPageLoad();
  } else {
    window.addEventListener("load", () => setTimeout(recordPageLoad, 0), { once: true });
  }

  // JS error counters
  window.addEventListener("error", () => { win.jsErrors++; });
  window.addEventListener("unhandledrejection", () => { win.jsErrors++; });

  // API latency via fetch wrapper (same-origin /api only; skip the beacon itself)
  const origFetch = window.fetch.bind(window);
  window.fetch = (input: RequestInfo | URL, init?: RequestInit) => {
    let url = "";
    try {
      url = typeof input === "string" ? input : input instanceof URL ? input.pathname : (input as Request).url;
    } catch {}
    const isApi = url.startsWith("/api") && !url.startsWith("/api/telemetry/beacon");
    if (!isApi) return origFetch(input, init);
    const start = performance.now();
    const done = () => {
      const ms = performance.now() - start;
      win.api.count++;
      win.api.totalMs += ms;
      if (ms > win.api.maxMs) win.api.maxMs = ms;
    };
    return origFetch(input, init).then(
      (res) => { done(); return res; },
      (err) => { done(); throw err; },
    );
  };

  const timer = setInterval(() => flush(false), FLUSH_INTERVAL_MS);
  // Flush on tab hide so short sessions still report.
  document.addEventListener("visibilitychange", () => {
    if (document.visibilityState === "hidden") flush(true);
  });
  // No teardown needed — this lives for the lifetime of the page.
  void timer;
}
